/**
 * Getting back in, and proving the address.
 *
 * Three screens that share a shape: forgot-password, reset-password, and
 * verify-email.
 *
 * **The forgot-password screen shows the same message whichever address is
 * typed.** That is not a UI simplification — the server deliberately refuses to
 * say whether an account exists, because an unauthenticated endpoint that
 * distinguished them would be a free membership oracle for anyone with a list of
 * addresses. The screen has to hold that line: a redesign that adds a helpful
 * "we don't have an account for that" undoes it entirely.
 */
import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { api, ApiError } from '../lib/api.js';
import { Link, navigate, useRoute } from '../lib/router.js';
import { useSession } from '../lib/session.js';

export function ForgotPasswordScreen(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);

    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      // Only a rate limit or a dead server reaches here. A missing account does
      // not — the endpoint answers 200 either way, on purpose.
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>

        {sent ? (
          <>
            <h1 className="auth-title">Check your email</h1>
            <p className="auth-sub">
              If an account exists for <strong>{email.trim()}</strong>, a reset link is on
              its way. It works once and expires in an hour.
            </p>
            <p className="field-hint">
              Nothing arrived? Check spam, then try again — and note that we deliberately
              do not confirm whether an address has an account, so this message looks the
              same either way.
            </p>
            <div className="settings-links">
              <Link to="/login" className="btn btn-primary">Back to sign in</Link>
              <button className="btn btn-quiet" onClick={() => setSent(false)}>
                Try another address
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="auth-title">Reset your password</h1>
            <p className="auth-sub">
              Enter the address on your account and we will send a link to set a new
              password.
            </p>

            <form onSubmit={onSubmit} noValidate>
              <label className="field">
                <span className="field-label">Email</span>
                <input
                  className="input" type="email" required autoComplete="email" autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </label>

              {error && <div className="form-error" role="alert">{error}</div>}

              <button className="btn btn-primary btn-block" type="submit" disabled={pending}>
                {pending ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p className="auth-alt">
              Remembered it? <Link to="/login">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordScreen(): JSX.Element {
  const { query } = useRoute();
  const token = query.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (pending) return;
    setError(null);

    if (password !== confirm) {
      setError('The passwords do not match.');
      return;
    }

    setPending(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // 410 Gone: the link is spent or expired. A retry with the same token
        // can never work, so the form is replaced rather than left inviting one.
        if (err.status === 410) setExpired(true);
      } else {
        setError('Could not reach the server.');
      }
    } finally {
      setPending(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">AEGIS</div>
          <h1 className="auth-title">Link incomplete</h1>
          <p className="auth-sub">
            This page needs the link from your email. Some mail clients break long
            links across lines — copying the whole thing usually fixes it.
          </p>
          <Link to="/forgot-password" className="btn btn-primary btn-block">
            Send a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>

        {done ? (
          <>
            <h1 className="auth-title">Password changed</h1>
            <p className="auth-sub">
              Every session was signed out, on this device and any others. Sign in with
              your new password.
            </p>
            <Link to="/login" className="btn btn-primary btn-block">Sign in</Link>
          </>
        ) : expired ? (
          <>
            <h1 className="auth-title">Link expired</h1>
            <p className="auth-sub">{error}</p>
            <Link to="/forgot-password" className="btn btn-primary btn-block">
              Send a new link
            </Link>
          </>
        ) : (
          <>
            <h1 className="auth-title">Choose a new password</h1>
            <p className="auth-sub">
              This signs out every session on the account, including any you did not
              start.
            </p>

            <form onSubmit={onSubmit} noValidate>
              <label className="field">
                <span className="field-label">New password</span>
                <input
                  className="input" type="password" required autoComplete="new-password" autoFocus
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                <span className={`field-hint ${password && password.length < 12 ? 'field-hint-warn' : ''}`}>
                  At least 12 characters. Length is the requirement — there is no
                  uppercase or symbol rule.
                </span>
              </label>

              <label className="field">
                <span className="field-label">Confirm new password</span>
                <input
                  className="input" type="password" required autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                />
              </label>

              {error && <div className="form-error" role="alert">{error}</div>}

              <button className="btn btn-primary btn-block" type="submit" disabled={pending}>
                {pending ? 'Changing…' : 'Change password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Confirms on load rather than behind a button.
 *
 * The user already clicked a link; asking them to click again to confirm the
 * click is friction with no benefit. The token is single-use, so a prefetching
 * mail client burning it is the one real cost — and the recovery is a resend,
 * which is one button away.
 */
export function VerifyEmailScreen(): JSX.Element {
  const { query } = useRoute();
  const { refresh, status } = useSession();
  const token = query.get('token') ?? '';

  const [state, setState] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed');
  const [message, setMessage] = useState(token ? '' : 'This link is missing its token.');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    void api.verifyEmail(token)
      .then(() => {
        if (cancelled) return;
        setState('done');
        // The cached user carries the flag; refresh so the banner disappears.
        void refresh();
      })
      .catch((e) => {
        if (cancelled) return;
        setState('failed');
        setMessage(e instanceof ApiError ? e.message : 'Could not reach the server.');
      });

    return () => { cancelled = true; };
  }, [token, refresh]);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">AEGIS</div>

        {state === 'working' && (
          <>
            <h1 className="auth-title">Confirming…</h1>
            <p className="auth-sub">One moment.</p>
          </>
        )}

        {state === 'done' && (
          <>
            <h1 className="auth-title">Address confirmed</h1>
            <p className="auth-sub">
              We can now reach you about password resets and billing. That is all we
              will use it for.
            </p>
            <Link to="/" className="btn btn-primary btn-block">Open the chart</Link>
          </>
        )}

        {state === 'failed' && (
          <>
            <h1 className="auth-title">Could not confirm</h1>
            <p className="auth-sub">{message}</p>
            <p className="field-hint">
              Confirmation links work once. If you have already confirmed, nothing is
              wrong. Otherwise sign in and ask for a new one from Settings.
            </p>
            <div className="settings-links">
              <Link to={status === 'anonymous' ? '/login' : '/settings'} className="btn btn-primary">
                {status === 'anonymous' ? 'Sign in' : 'Settings'}
              </Link>
              <button className="btn btn-quiet" onClick={() => navigate('/')}>
                Open the chart
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
